import { useCallback, useEffect, useRef } from "react";

import { deepEquals } from "@swa-ui/object";

import { window } from "../window";

type EventType = keyof WindowEventMap;
type WindowEventListenerType<T extends EventType> = (event?: WindowEventMap[T]) => void;

/**
 * A hook that adds an event listener to the window for a given event type. This hook
 * manages the listener so that stale functions are not left on the window object.
 *
 * @param eventType The type of the event to listen for such as "keydown" or "click"
 * @param listener The event handler to add to the window
 * @param options Any additional options for the event or a boolean for `useCapture`
 */
export const useWindowEventListener = <T extends EventType>(
  eventType: T,
  listener: WindowEventListenerType<T>,
  options?: boolean | AddEventListenerOptions
) => {
  const listenerCallback = useCallback(listener, [listener]);
  const eventTypeRef = useRef(eventType);
  const callbackRef = useRef<typeof listener | null>(null);
  const optionsRef = useRef(options);

  useEffect(() => {
    if (shouldRefreshEventListener()) {
      callbackRef.current &&
        window.removeEventListener(eventTypeRef.current, callbackRef.current, optionsRef.current);
      window.addEventListener(eventType, listenerCallback, options);

      eventTypeRef.current = eventType;
      callbackRef.current = listenerCallback;
      optionsRef.current = options;
    }

    return () => {
      window.removeEventListener(eventType, listenerCallback, options);
    };
  }, [eventType, listenerCallback, options]);

  function shouldRefreshEventListener() {
    const eventChanged = eventType !== eventTypeRef.current;
    const callbackChanged = listenerCallback !== callbackRef.current;
    const optionsEqual = deepEquals(options, optionsRef.current);

    return eventChanged || callbackChanged || !optionsEqual;
  }
};
